import React, { useState, useMemo, useEffect } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { createBacktest, runBacktest, getBacktest, getComparableBacktests, createStrategy } from '../../api';
import Button from '../../components/Button';
import {
  extractNumericParams,
  applyParamOverrides,
  numericFieldLabel,
  nodeTitleFromParam,
  fmt$,
  fmtBarDate,
  VARIANT_COLORS,
} from './utils';

const VARIANT_LABELS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
const MAX_VARIANTS = VARIANT_LABELS.length;

// Metric rows for the comparison matrix. `better` drives best-cell highlighting
// and whether a delta-vs-baseline reads as an improvement; `unit` drives formatting.
const METRICS = [
  { label: 'Return', unit: 'pct', better: 'higher', get: (r) => num(r.return_pct) },
  { label: 'Net Profit', unit: 'money', better: 'higher', get: (r) => num(r.net_profit) },
  { label: 'Sharpe Ratio', unit: 'num', better: 'higher', get: (r) => num(r.sharpe_ratio) },
  { label: 'Max Drawdown', unit: 'pct', better: 'lower', get: (r) => num(r.max_drawdown_pct) },
  { label: 'Total Trades', unit: 'int', better: null, get: (r) => num(r.total_trades) },
  {
    label: 'Win Rate', unit: 'pct', better: 'higher',
    get: (r) => {
      const w = num(r.won_trades) ?? 0;
      const l = num(r.lost_trades) ?? 0;
      const t = w + l;
      return t ? Math.round((1000 * w) / t) / 10 : null;
    },
  },
  { label: 'Excess vs Buy & Hold', unit: 'pct', better: 'higher', get: (r) => num(r.excess_return_pct) },
  { label: 'Alpha (annualized)', unit: 'pct', better: 'higher', get: (r) => num(r.alpha_pct) },
  { label: 'Beta', unit: 'num', better: null, get: (r) => num(r.beta) },
  { label: 'Total Commission', unit: 'money', better: 'lower', get: (r) => num(r.total_commission_cost) },
  { label: 'Total Slippage', unit: 'money', better: 'lower', get: (r) => num(r.total_slippage_cost) },
];

function num(v) {
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function fmtMetric(unit, v) {
  if (v == null) return '—';
  if (unit === 'money') return fmt$(v);
  if (unit === 'pct') return `${v}%`;
  if (unit === 'int') return String(v);
  return String(v);
}

function fmtDelta(unit, delta) {
  if (delta == null || delta === 0) return null;
  const sign = delta > 0 ? '+' : '−';
  const abs = Math.abs(delta);
  if (unit === 'money') return `${sign}${fmt$(abs)}`;
  if (unit === 'pct') return `${sign}${round(abs)}%`;
  if (unit === 'int') return `${sign}${abs}`;
  return `${sign}${round(abs)}`;
}

function round(v) {
  return Math.round(v * 1000) / 1000;
}

export default function ComparePanel({ backtest, strategyForEdit }) {
  const strategyId = backtest?.strategy_id || backtest?.strategy?.id || backtest?.strategy;

  // Baseline (variant A) is always the current backtest and can't be removed.
  const [variants, setVariants] = useState([]);
  const [openForm, setOpenForm] = useState(null); // null | 'create' | 'pick'
  // Per-variant "save as strategy" state, keyed by variant key.
  const [promote, setPromote] = useState({}); // key -> { status, name?, msg? }

  // Reset the comparison whenever the baseline backtest changes (e.g. a rerun).
  useEffect(() => {
    if (!backtest?.id) { setVariants([]); return; }
    setVariants([{ key: String(backtest.id), backtest }]);
    setOpenForm(null);
    setPromote({});
  }, [backtest?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // `graph` is only present for inline-created variants (the modified graph that
  // produced them) — that's what makes them promotable to a saved strategy.
  const addVariant = (bt, graph) => {
    setVariants((prev) => {
      if (prev.some((v) => v.key === String(bt.id))) return prev;
      if (prev.length >= MAX_VARIANTS) return prev;
      return [...prev, { key: String(bt.id), backtest: bt, graph: graph || null }];
    });
    setOpenForm(null);
  };

  const removeVariant = (key) => {
    setVariants((prev) => prev.filter((v, i) => i === 0 || v.key !== key));
  };

  const promoteVariant = async (variant, label) => {
    if (!variant.graph) return;
    setPromote((p) => ({ ...p, [variant.key]: { status: 'saving' } }));
    try {
      const name = `${strategyForEdit?.name || 'Strategy'} (Variant ${label})`;
      const resp = await createStrategy({
        name,
        description: strategyForEdit?.description || '',
        graph: variant.graph,
      });
      setPromote((p) => ({ ...p, [variant.key]: { status: 'saved', name: resp.data?.name || name } }));
    } catch (err) {
      setPromote((p) => ({ ...p, [variant.key]: { status: 'error', msg: err.response?.data?.detail || 'Could not save.' } }));
    }
  };

  const atCapacity = variants.length >= MAX_VARIANTS;

  return (
    <div style={{ marginBottom: 'var(--space-5)' }}>
      <div className="section-title">A/B Comparison</div>
      <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--muted)', marginBottom: 'var(--space-3)' }}>
        Stack this backtest (baseline A) against variants — either tweak parameters and rerun,
        or pull in a saved run — and compare their metrics and equity side by side.
      </div>

      {/* Variant chips */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)', marginBottom: 'var(--space-3)' }}>
        {variants.map((v, i) => (
          <div
            key={v.key}
            style={{
              display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
              background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-md)', padding: '6px 10px', fontSize: 'var(--fs-sm)',
            }}
          >
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: VARIANT_COLORS[i % VARIANT_COLORS.length], flexShrink: 0 }} />
            <span style={{ fontWeight: 700, color: 'var(--text)' }}>{VARIANT_LABELS[i]}</span>
            <span style={{ color: 'var(--muted)' }}>
              {v.backtest.symbol}{i === 0 ? ' · baseline' : ''}
            </span>
            {v.graph && (
              promote[v.key]?.status === 'saved' ? (
                <span style={{ color: 'var(--green)', fontSize: 'var(--fs-xs)', fontWeight: 600 }}>✓ saved</span>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  loading={promote[v.key]?.status === 'saving'}
                  onClick={() => promoteVariant(v, VARIANT_LABELS[i])}
                  title="Save this variant as a new strategy"
                  style={{ padding: '4px 8px', minHeight: 26, fontSize: 'var(--fs-xs)' }}
                >
                  Save as strategy
                </Button>
              )
            )}
            {i !== 0 && (
              <button
                className="icon-btn"
                onClick={() => removeVariant(v.key)}
                title="Remove variant"
                style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 'var(--fs-lg)', lineHeight: 1, padding: '0 4px' }}
              >
                ×
              </button>
            )}
          </div>
        ))}
      </div>
      {Object.entries(promote).some(([, s]) => s.status === 'error' || s.status === 'saved') && (
        <div style={{ marginTop: -4, marginBottom: 'var(--space-3)', fontSize: 'var(--fs-xs)' }}>
          {Object.entries(promote).map(([key, s]) => {
            const idx = variants.findIndex((v) => v.key === key);
            const label = idx >= 0 ? VARIANT_LABELS[idx] : '?';
            if (s.status === 'error') return <div key={key} style={{ color: 'var(--red)' }}>Variant {label}: {s.msg}</div>;
            if (s.status === 'saved') return <div key={key} style={{ color: 'var(--muted)' }}>Variant {label} saved as “{s.name}” — find it in your strategies.</div>;
            return null;
          })}
        </div>
      )}

      {/* Add-variant controls */}
      <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap', marginBottom: 'var(--space-4)' }}>
        <Button
          variant={openForm === 'create' ? 'primary' : 'ghost'}
          disabled={atCapacity}
          onClick={() => setOpenForm((f) => (f === 'create' ? null : 'create'))}
        >
          + Create Variant
        </Button>
        <Button
          variant={openForm === 'pick' ? 'primary' : 'ghost'}
          disabled={atCapacity}
          onClick={() => setOpenForm((f) => (f === 'pick' ? null : 'pick'))}
        >
          + Add Saved Run
        </Button>
        {atCapacity && (
          <span style={{ alignSelf: 'center', fontSize: 'var(--fs-xs)', color: 'var(--muted)' }}>
            Maximum of {MAX_VARIANTS} variants.
          </span>
        )}
      </div>

      {openForm === 'create' && (
        <CreateVariantForm
          backtest={backtest}
          strategyForEdit={strategyForEdit}
          strategyId={strategyId}
          existingKeys={variants.map((v) => v.key)}
          onCreated={addVariant}
        />
      )}
      {openForm === 'pick' && (
        <SavedRunPicker
          strategyId={strategyId}
          existingKeys={variants.map((v) => v.key)}
          onPicked={addVariant}
        />
      )}

      <ComparisonMatrix variants={variants} />
      <ComparisonEquityChart variants={variants} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create variant: tweak numeric params and rerun against a modified graph copy
// (via the run endpoint's param_overrides — never mutates the saved strategy).
// ---------------------------------------------------------------------------
function CreateVariantForm({ backtest, strategyForEdit, strategyId, existingKeys, onCreated }) {
  const params = useMemo(() => extractNumericParams(strategyForEdit?.graph), [strategyForEdit]);
  const [values, setValues] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  useEffect(() => {
    const m = {};
    params.forEach((p) => { m[p.key] = p.value; });
    setValues(m);
  }, [params]);

  const groups = useMemo(() => {
    const g = {};
    params.forEach((p) => {
      if (!g[p.nodeId]) g[p.nodeId] = { nodeId: p.nodeId, label: nodeTitleFromParam(p), params: [] };
      g[p.nodeId].params.push(p);
    });
    return Object.values(g).sort((a, b) => a.label.localeCompare(b.label));
  }, [params]);

  const runVariant = async () => {
    if (!strategyId) { setError('Strategy is still loading — try again in a moment.'); return; }
    setBusy(true); setError(''); setInfo('');
    try {
      // Only send params the user actually changed.
      const overrides = {};
      params.forEach((p) => {
        const parsed = Number(values[p.key]);
        if (Number.isFinite(parsed) && parsed !== p.value) overrides[p.key] = parsed;
      });
      if (Object.keys(overrides).length === 0) {
        setError('Change at least one parameter to create a variant.');
        setBusy(false);
        return;
      }

      const createResp = await createBacktest({
        strategy: strategyId,
        asset_type: backtest.asset_type,
        symbol: backtest.symbol,
        start_date: backtest.start_date,
        end_date: backtest.end_date,
        initial_cash: backtest.initial_cash,
        slippage_perc: backtest.slippage_perc,
        commission_perc: backtest.commission_perc,
        fill_on_close: backtest.fill_on_close,
        fx_interval: backtest.fx_interval,
      });

      const runResp = await runBacktest(createResp.data.id, { param_overrides: overrides });
      if (runResp.status === 202) {
        setInfo('This variant is large and was queued to run in the background. Open it from your history once it finishes, then add it via "Add Saved Run".');
        return;
      }
      if (existingKeys.includes(String(runResp.data.id))) {
        setInfo('That variant is already in the comparison.');
        return;
      }
      // Pass the overridden graph so the variant can be promoted to a saved strategy.
      const variantGraph = applyParamOverrides(strategyForEdit?.graph, params, values);
      onCreated(runResp.data, variantGraph);
    } catch (err) {
      const data = err.response?.data;
      if (data?.requires_signup) setError(data.detail || 'Sign up to run more backtests.');
      else setError(data?.detail || err.message || 'Failed to run variant.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={panelStyle}>
      {!strategyForEdit && <div style={{ color: 'var(--muted)', fontSize: 12 }}>Loading strategy…</div>}
      {strategyForEdit && params.length === 0 && (
        <div style={{ color: 'var(--muted)', fontSize: 12 }}>This strategy has no numeric parameters to vary.</div>
      )}
      {params.length > 0 && (
        <>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 10 }}>
            Adjust one or more parameters, then run. The variant uses the same symbol, dates,
            cash and costs as the baseline — only the parameters below differ.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 10 }}>
            {groups.map((group) => (
              <div key={group.nodeId} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 10, background: 'var(--card)' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)', marginBottom: 8, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {group.label}
                </div>
                <div style={{ display: 'grid', gap: 8 }}>
                  {group.params.map((p) => (
                    <label key={p.key} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <span style={{ fontSize: 11, color: 'var(--muted)' }}>{numericFieldLabel(p)}</span>
                      <input
                        type="number"
                        value={values[p.key] ?? p.value}
                        onChange={(e) => setValues((prev) => ({ ...prev, [p.key]: e.target.value }))}
                        style={inputStyle}
                      />
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 'var(--space-3)' }}>
            <Button variant="primary" loading={busy} onClick={runVariant}>
              {busy ? 'Running…' : 'Run Variant'}
            </Button>
          </div>
        </>
      )}
      {error && <div style={{ marginTop: 'var(--space-2)', color: 'var(--red)', fontSize: 'var(--fs-sm)' }}>{error}</div>}
      {info && <div style={{ marginTop: 'var(--space-2)', color: 'var(--teal)', fontSize: 'var(--fs-sm)' }}>{info}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Saved run picker: lightweight list of comparable completed backtests; the
// full record (with equity curve) is fetched only when one is selected.
// ---------------------------------------------------------------------------
function SavedRunPicker({ strategyId, existingKeys, onPicked }) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState('');
  const [addingId, setAddingId] = useState(null);

  useEffect(() => {
    let mounted = true;
    setError('');
    getComparableBacktests(strategyId ? { strategy: strategyId } : undefined)
      .then((res) => { if (mounted) setItems(res.data || []); })
      .catch(() => { if (mounted) { setItems([]); setError('Could not load saved runs.'); } });
    return () => { mounted = false; };
  }, [strategyId]);

  const selectable = (items || []).filter((it) => !existingKeys.includes(String(it.id)));

  const pick = async (item) => {
    setAddingId(item.id); setError('');
    try {
      const res = await getBacktest(item.id);
      onPicked(res.data);
    } catch {
      setError('Could not load that backtest.');
    } finally {
      setAddingId(null);
    }
  };

  return (
    <div style={panelStyle}>
      {items === null && <div style={{ color: 'var(--muted)', fontSize: 12 }}>Loading saved runs…</div>}
      {items !== null && selectable.length === 0 && (
        <div style={{ color: 'var(--muted)', fontSize: 12 }}>No other completed runs of this strategy to compare.</div>
      )}
      {selectable.length > 0 && (
        <div style={{ display: 'grid', gap: 6, maxHeight: 260, overflowY: 'auto' }}>
          {selectable.map((it) => (
            <button
              key={it.id}
              disabled={addingId === it.id}
              onClick={() => pick(it)}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                textAlign: 'left', background: 'var(--card)', border: '1px solid var(--border)',
                borderRadius: 8, padding: '8px 10px', cursor: 'pointer', color: 'var(--text)', fontSize: 12,
              }}
            >
              <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                <strong>{it.symbol}</strong>
                <span style={{ color: 'var(--muted)' }}> · {it.start_date} → {it.end_date}</span>
              </span>
              <span style={{ display: 'flex', gap: 10, flexShrink: 0, fontFamily: 'var(--font-mono)' }}>
                <span style={{ color: (it.return_pct ?? 0) >= 0 ? 'var(--green)' : 'var(--red)' }}>
                  {it.return_pct != null ? `${it.return_pct}%` : '—'}
                </span>
                <span style={{ color: 'var(--muted)' }}>Sharpe {it.sharpe_ratio ?? '—'}</span>
                {addingId === it.id && <span style={{ color: 'var(--teal)' }}>adding…</span>}
              </span>
            </button>
          ))}
        </div>
      )}
      {error && <div style={{ marginTop: 8, color: 'var(--red)', fontSize: 12 }}>{error}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Metrics matrix: metric rows × variant columns, best cell highlighted per row,
// delta-vs-baseline shown on non-baseline cells.
// ---------------------------------------------------------------------------
function ComparisonMatrix({ variants }) {
  const cols = variants.map((v, i) => ({
    key: v.key,
    label: VARIANT_LABELS[i],
    color: VARIANT_COLORS[i % VARIANT_COLORS.length],
    results: v.backtest.results || {},
    isBaseline: i === 0,
  }));

  return (
    <div style={{ overflowX: 'auto', marginTop: 8 }}>
      <table style={{ width: '100%', minWidth: 360, borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr>
            <th style={{ ...cellStyle, textAlign: 'left', color: 'var(--muted)', fontWeight: 600 }}>Metric</th>
            {cols.map((c) => (
              <th key={c.key} style={{ ...cellStyle, textAlign: 'right' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
                  <span style={{ width: 9, height: 9, borderRadius: '50%', background: c.color }} />
                  <span style={{ color: 'var(--text)', fontWeight: 700 }}>{c.label}</span>
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {METRICS.map((m) => {
            const raw = cols.map((c) => m.get(c.results));
            const baseVal = raw[0];
            // Best value across variants (only when direction is defined).
            let bestIdx = -1;
            if (m.better) {
              raw.forEach((val, i) => {
                if (val == null) return;
                if (bestIdx === -1) { bestIdx = i; return; }
                const cur = raw[bestIdx];
                if (m.better === 'higher' ? val > cur : val < cur) bestIdx = i;
              });
            }
            return (
              <tr key={m.label} style={{ borderTop: '1px solid var(--border)' }}>
                <td style={{ ...cellStyle, textAlign: 'left', color: 'var(--muted)' }}>{m.label}</td>
                {cols.map((c, i) => {
                  const val = raw[i];
                  const isBest = m.better && i === bestIdx && val != null && cols.length > 1;
                  const delta = (!c.isBaseline && val != null && baseVal != null) ? round(val - baseVal) : null;
                  const deltaStr = fmtDelta(m.unit, delta);
                  const improved = delta == null || !m.better
                    ? null
                    : (m.better === 'higher' ? delta > 0 : delta < 0);
                  return (
                    <td key={c.key} style={{ ...cellStyle, textAlign: 'right', background: isBest ? 'rgba(34, 197, 94, 0.12)' : 'transparent' }}>
                      <div style={{ fontFamily: 'var(--font-mono)', fontWeight: isBest ? 700 : 500, color: 'var(--text)' }}>
                        {fmtMetric(m.unit, val)}
                      </div>
                      {deltaStr && (
                        <div style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: improved == null ? 'var(--muted)' : improved ? 'var(--green)' : 'var(--red)' }}>
                          {deltaStr}
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Equity overlay: each variant normalized to % return from its own start so
// runs with different starting cash are comparable. Series merged by date.
// ---------------------------------------------------------------------------
function ComparisonEquityChart({ variants }) {
  const data = useMemo(() => {
    const byDate = {};
    variants.forEach((v) => {
      const curve = v.backtest.equity_curve || [];
      const start = curve.length ? Number(curve[0].value) : null;
      if (!start) return;
      curve.forEach((pt) => {
        const pct = Math.round((((Number(pt.value) / start) - 1) * 100) * 1000) / 1000;
        if (!byDate[pt.date]) byDate[pt.date] = { date: pt.date };
        byDate[pt.date][v.key] = pct;
      });
    });
    return Object.values(byDate).sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
  }, [variants]);

  if (data.length === 0) return null;

  return (
    <div style={{ marginTop: 'var(--space-5)' }}>
      <h3 className="section-title">Equity Curves (normalized % return)</h3>
      <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--muted)', marginBottom: 'var(--space-2)' }}>
        Each curve is indexed to its own starting value, so variants with different starting cash line up.
      </div>
      <ResponsiveContainer width="100%" height={280}>
        <LineChart data={data} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
          <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'var(--muted)' }} interval="preserveStartEnd" tickFormatter={fmtBarDate} />
          <YAxis tick={{ fontSize: 11, fill: 'var(--muted)' }} tickFormatter={(v) => `${v}%`} domain={['auto', 'auto']} />
          <Tooltip
            formatter={(val, name) => [`${val}%`, name]}
            labelFormatter={fmtBarDate}
            contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)' }}
          />
          <Legend />
          {variants.map((v, i) => (
            <Line
              key={v.key}
              type="monotone"
              dataKey={v.key}
              name={`${VARIANT_LABELS[i]} · ${v.backtest.symbol}`}
              stroke={VARIANT_COLORS[i % VARIANT_COLORS.length]}
              dot={false}
              strokeWidth={2}
              connectNulls
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

const panelStyle = {
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-lg)',
  background: 'var(--surface)',
  padding: 'var(--space-3)',
  marginBottom: 'var(--space-4)',
};

const inputStyle = {
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--card)',
  color: 'var(--text)',
  padding: '6px 8px',
  fontSize: 'var(--fs-md)',
  fontFamily: 'var(--font-mono)',
};

const cellStyle = {
  padding: '8px 10px',
  verticalAlign: 'top',
};
